import axios from "axios";
import type {
  AssociationResponse,
  AssociationTxnResponse,
  AuthorizeDerivedKeyResponse,
  CreateFollowTxnRequestParams,
  MsgDeSoTxn,
  SubmitTransactionResponse,
} from "deso-protocol";
import { createAccessGroup } from "deso-protocol";
import { identity, updateFollowingStatus } from "deso-protocol";
import { random } from "es-toolkit";

import { waitForTxn } from "@/backend/api-helpers";
import type { UserSocialProfiles } from "@/contexts/user-context";
import {
  getFocusAuthHeaders,
  getFocusAuthHeadersAsync,
  getOptionalFocusAuthHeadersAsync,
} from "@/utils/api-utils";
import type { SOCIALS_LIST, USERSTATUS } from "@/utils/constants";
import { DEFAULT_MESSAGING_GROUP_KEY_NAME } from "@/utils/constants";
import { APP_ROUTES, SocialNetworkType } from "@/utils/constants";
import type { Dictionary } from "@/utils/helpers";

import type {
  GetNotificationParams,
  NotificationRequestResponse,
} from "./notifications-api";
import {
  getEarningsRequest,
  getNotificationsRequest,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "./notifications-api";
import {
  NotificationCategory,
  NotificationCategoryLabel,
  NotificationCategoryToSubCategories,
  NotificationOrderBy,
  NotificationStatus,
  NotificationStatusLabels,
  NotificationSubCategory,
  NotificationSubCategoryLabels,
  NotificationTransmissionType,
  ReferralNotificationSubcategory,
} from "./notifications-constants";

export {
  getEarningsRequest,
  getNotificationsRequest,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  NotificationCategory,
  NotificationCategoryLabel,
  NotificationCategoryToSubCategories,
  NotificationOrderBy,
  NotificationStatus,
  NotificationStatusLabels,
  NotificationSubCategory,
  NotificationSubCategoryLabels,
  NotificationTransmissionType,
  ReferralNotificationSubcategory,
};

export type { GetNotificationParams, NotificationRequestResponse };

export const focusAPIInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_FOCUS_EXTERNAL_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const focusAPIV1Instance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_FOCUS_EXTERNAL_API_V1_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const focusInternalAPIInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_INTERNAL_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export interface PlatformJoiningBonus {
  Platform: string;
  Username: string;
  Status: string;
  FocusTokenRewardUsdCents: number;
  PublicKey: string;
}

/*
Verify Referral Code
Check to see if a referral code is valid
 */
export interface VerifyReferralCodeRequest {
  PublicKey: string;
  ReferralCode: string;
}
export interface VerifyReferralCodeResponse {
  PublicKey: string;
  ReferralStatus: string;
}
export const verifyReferralCode = async (
  payload: VerifyReferralCodeRequest,
) => {
  const { data } = await focusAPIInstance.post<VerifyReferralCodeResponse>(
    "referral-codes/verify",
    payload,
  );
  return data;
};

/*
Joining Bonus Twitter
The callback to call after authorizing a Twitter account
*/
export interface JoiningBonusTwitterOAuthRequest {
  PublicKey: string;
}
export interface JoiningBonusTwitterCallbackResponse {
  OAuthConsumerKey: string;
  OAuthConsumerSecret: string;
}
export const joiningBonusTwitterOAuth = async (
  payload: JoiningBonusTwitterOAuthRequest,
) => {
  const { data } =
    await focusAPIInstance.post<JoiningBonusTwitterCallbackResponse>(
      "twitter/oauth",
      payload,
    );
  return data;
};

/*
Joining Bonus Twitter
The callback to call after authorizing a Twitter account
*/
export interface JoiningBonusTwitterCallbackRequest {
  PublicKey: string;
  OauthToken: string;
  OauthVerifier: string;
}
export interface JoiningBonusTwitterCallbackResponse {
  PublicKey: string;
  Status: string;
}
export const joiningBonusTwitterCallback = async (
  payload: JoiningBonusTwitterCallbackRequest,
) => {
  const { data } =
    await focusAPIInstance.post<JoiningBonusTwitterCallbackResponse>(
      "twitter/callback",
      payload,
    );
  return data;
};

/*
Joining Bonus Status
Used to check on the status of a single joining bonus
_Note_: this endpoint should be polled until the status received is “success”
 */
export interface JoiningBonusStatusRequest {
  Platform: string; // e.g. “twitter”, “instagram”, etc
  PublicKey: string;
}
export interface JoiningBonusStatusResponse {
  PublicKey: string;
  PlatformJoiningBonus: PlatformJoiningBonus;
}
export const joiningBonusStatus = async ({
  Platform,
  PublicKey,
}: JoiningBonusStatusRequest) => {
  const { data } = await focusAPIInstance.get<JoiningBonusStatusResponse>(
    `joining-bonus/${Platform}/${PublicKey}`,
  );
  return data;
};

/*
Joining Bonuses By PublicKey
Get all joining bonuses for a public key
 */
export interface JoiningBonusesByPublicKeyRequest {
  PublicKey: string;
}
export interface JoiningBonusesByPublicKeyResponse {
  PublicKey: string;
  JoiningBonuses: PlatformJoiningBonus;
}
export const joiningBonusesByPublicKey = async ({
  PublicKey,
}: JoiningBonusesByPublicKeyRequest) => {
  const { data } =
    await focusAPIInstance.get<JoiningBonusesByPublicKeyResponse>(
      `joining-bonus/status/${PublicKey}`,
    );
  return data;
};

/*
Claim Joining Bonus
Claim a joining bonus in the form of a user association reserving focus tokens
 */
export interface ClaimJoiningBonusRequest {
  PublicKey: string;
  Platform: string;
}
export interface ClaimJoiningBonusResponse {
  AssociationTransaction: AssociationTxnResponse;
  PlatformJoiningBonus: PlatformJoiningBonus;
}
export const claimJoiningBonus = async ({
  PublicKey,
}: ClaimJoiningBonusRequest) => {
  const { data } = await focusAPIInstance.post<ClaimJoiningBonusResponse>(
    `joining-bonus/status/${PublicKey}`,
  );
  return data;
};

/*
Push Notification Subscription
Create a push notification subscription that the API can use to push notifications to the user's client.
 */

export enum PushNotificationRecordStatus {
  ACTIVE = "ACTIVE",
  DISABLED = "DISABLED",
}

export interface PushNotificationPayload {
  UserPublicKey: string;
  Endpoint: string;
  AuthKey: string;
  P256dhKey: string;
  FingerPrint: string;
  Status: PushNotificationRecordStatus;
}

export const upsertPushNotificationSubscription = async (
  payload: PushNotificationPayload,
) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `push-notification-subscription`,
    payload,
    getFocusAuthHeaders(jwt, payload.UserPublicKey),
  );
  return data;
};

export const getPushNotificationSubscriptionRecord = async ({
  fingerprint,
  publicKey,
}: {
  fingerprint: string;
  publicKey: string;
}) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get(
    `push-notification-subscription/${fingerprint}`,
    getFocusAuthHeaders(jwt, publicKey),
  );
  return data;
};

export type TwitterUserValueResponse = {
  Tweets: Array<{
    TweetId: string;
    AuthorId: string;
    Text: string;
    RetweetCount: number;
    ReplyCount: number;
    LikeCount: number;
    QuoteCount: number;
    ImpressionCount: number;
    BookmarkCount: number;
    CreatedAt: string;
    UpdatedAt: string;
    Version: string;
  }>;
  User: {
    CreatedAt: string;
    UserId: string;
    Name: string;
    ProfileImageUrl: string;
    Protected: boolean;
    FollowersCount: number;
    FollowingCount: number;
    ListedCount: number;
    TweetCount: number;
    Username: string;
    Verified: boolean;
    VerifiedType: VerifiedType;
    JoiningRewardUsdCents: number;
    ReferralRewardUsdCents: number;
  };
  value: number;
};

export const getUserPrecalculatedRewardsInternal = async (
  username: string,
): Promise<TwitterUserValueResponse | null> => {
  try {
    const res = await focusInternalAPIInstance.get(
      `twitter/user/${username}?&cacheOnly=true`,
    );

    if (res.data.User.ProfileImageUrl) {
      res.data.User.ProfileImageUrl = res.data.User.ProfileImageUrl.replace(
        "_normal",
        "_400x400",
      );
    }

    return res.data;
  } catch (error) {
    console.error("Error fetching user precalculated rewards", error);
    return null;
  }
};

export const countPageView = async (
  host: string,
  route: string,
  params: Array<string>,
  publicKey?: string,
  jwt?: string,
) => {
  try {
    const isLoggedUser = publicKey && jwt;

    const res = await focusAPIInstance.post(
      "page/view",
      {
        host,
        route,
        ...params.reduce(
          (acc, curr, index) => ({ ...acc, [`pathParam${index}`]: curr }),
          {},
        ),
      },
      isLoggedUser ? getFocusAuthHeaders(jwt, publicKey) : {},
    );

    return res.data;
  } catch (error) {
    console.error(`Error counting page view`, error);
    return null;
  }
};

type AuthTwitterDTO = {
  publicKey: string;
  jwt: string;
  redirectUrl?: string;
};

export const authWithTwitter = async ({
  publicKey,
  jwt,
  redirectUrl,
}: AuthTwitterDTO) => {
  try {
    const env = redirectUrl || process.env.NEXT_PUBLIC_TWITTER_REDIRECT_ENV;
    const params = "?redirectUrl=" + env;
    const res = await focusAPIInstance.get(
      `twitter/oauth/login-url${params}`,
      getFocusAuthHeaders(jwt, publicKey),
    );

    const { Url } = res.data;
    return Url;
  } catch (error) {
    console.error(
      "An error happened when authenticating with Twitter auth",
      error,
    );
    return null;
  }
};

type SetAccessTokenDTO = {
  publicKey: string;
  jwt: string;
  state: string;
  code: string;
  redirectUrl?: string;
};

export type GenerateTwitterOauthTokenResponse = {
  Username: string;
  Verified: boolean;
  JoiningRewardUsdCents: number;
  Name: string;
  Description: string;
  ProfileImageUrl: string;
  ProfileBannerUrl: string;
};

export const generateTwitterOauthToken = async ({
  publicKey,
  jwt,
  state,
  code,
  redirectUrl,
}: SetAccessTokenDTO) => {
  const env = redirectUrl || process.env.NEXT_PUBLIC_TWITTER_REDIRECT_ENV;
  const params = "?redirectUrl=" + env;

  const res = await focusAPIInstance.post<GenerateTwitterOauthTokenResponse>(
    `twitter/oauth/access-token${params}`,
    {
      state,
      code,
    },
    getFocusAuthHeaders(jwt, publicKey),
  );

  return res.data;
};

export type PostViewRequest = {
  PostHash: string;
  PublicKey: string;
};

type SendPostViewsOptions = {
  keepalive?: boolean;
  jwt?: string;
};

const getFocusApiUrl = (path: string) => {
  const baseUrl = process.env.NEXT_PUBLIC_FOCUS_EXTERNAL_API_URL;

  if (!baseUrl) {
    throw new Error("Focus API URL is not configured");
  }

  return new URL(
    path,
    baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`,
  ).toString();
};

export const sendPostViews = async (
  postViews: PostViewRequest[],
  publicKey: string,
  { keepalive = false, jwt: providedJwt }: SendPostViewsOptions = {},
) => {
  if (postViews.length === 0) {
    return;
  }

  const jwt = providedJwt ?? (await identity.jwt());
  const authHeaders = getFocusAuthHeaders(jwt, publicKey).headers;
  const body = {
    PostViews: postViews,
  };

  if (keepalive && typeof fetch !== "undefined") {
    const headers = new Headers({
      "Content-Type": "application/json",
    });

    for (const [key, value] of Object.entries(authHeaders)) {
      if (value) {
        headers.set(key, value);
      }
    }

    const response = await fetch(getFocusApiUrl("post/views"), {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    });

    if (!response.ok) {
      throw new Error(`Failed to send post views (${response.status})`);
    }

    return;
  }

  await focusAPIInstance.post(
    "post/views",
    body,
    getFocusAuthHeaders(jwt, publicKey),
  );
};

export const sendPostView = async (postHash: string, publicKey: string) => {
  await sendPostViews(
    [
      {
        PostHash: postHash,
        PublicKey: publicKey,
      },
    ],
    publicKey,
  );
};

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export type RequestRecord = {
  username: string;
  balance: number;
  request: number;
  network: SocialNetworkType;
};

export const getBountyRequests = async (
  baseAmountCents: number,
): Promise<RequestRecord[]> => {
  await delay(100);

  const requests = [
    {
      username: "brunks",
      balance: 9707.6,
      request: 9494.6,
      network: SocialNetworkType.twitter,
    },
    {
      username: "TplusZero",
      balance: 9067.4,
      request: 8874.4,
      network: SocialNetworkType.twitter,
    },
    {
      username: "nader",
      balance: 8488.91,
      request: 8417.91,
      network: SocialNetworkType.twitter,
    },
    {
      username: "0xAustin",
      balance: 8081.68,
      request: 7985.68,
      network: SocialNetworkType.twitter,
    },
    {
      username: "BenErsing",
      balance: 7707.62,
      request: 7620.62,
      network: SocialNetworkType.twitter,
    },
    {
      username: "leojay",
      balance: 6557.64,
      request: 6486.64,
      network: SocialNetworkType.twitter,
    },
    {
      username: "senpai",
      balance: 5996.88,
      request: 5772.88,
      network: SocialNetworkType.twitter,
    },
    {
      username: "artz",
      balance: 5096.89,
      request: 5038.89,
      network: SocialNetworkType.twitter,
    },
    {
      username: "dharmesh",
      balance: 4973.05,
      request: 4865.05,
      network: SocialNetworkType.twitter,
    },
    {
      username: "lukelintz",
      balance: 4887.47,
      request: 4658.47,
      network: SocialNetworkType.twitter,
    },
    {
      username: "jordanlintz",
      balance: 4326.61,
      request: 4080.61,
      network: SocialNetworkType.twitter,
    },
  ];

  const baseAmount = baseAmountCents / 100;

  const updatedRequests = requests.map((request) => ({
    ...request,
    request: baseAmount * random(0.8, 1.2),
  }));

  return updatedRequests.sort((a, b) => b.request - a.request);
};

export const createFollowTx = async (payload: CreateFollowTxnRequestParams) => {
  const response = await updateFollowingStatus(payload);
  return response;
};

export enum DerivedKeyType {
  SUBSCRIPTION_CREATOR = "SUBSCRIPTION_CREATOR",
  SUBSCRIPTION_MEMBER = "SUBSCRIPTION_MEMBER",
  PAY_TO_VIEW_CREATOR = "PAY_TO_VIEW_CREATOR",
  GENERAL = "GENERAL",
  PAID_MESSAGE = "PAID_MESSAGE",
}
interface CreateDerivedKeyRequest {
  OwnerPublicKey: string;
  DerivedKeyType: DerivedKeyType;
  AccessGroupOwnerPublicKey?: string;
  AccessGroupKeyName?: string;
}
interface CreateDerivedKeyResponse {
  DerivedPublicKeyBase58Check: string;
  AlreadyExists?: boolean;
}
export const createDerivedKey = async (payload: CreateDerivedKeyRequest) => {
  const { data } = await focusAPIInstance.post<CreateDerivedKeyResponse>(
    "derived-key",
    payload,
    await getFocusAuthHeadersAsync(payload.OwnerPublicKey),
  );
  return data;
};

interface CreateSubscriptionTierRequest {
  CreatorPublicKey: string;
  DerivedPublicKey: string;
  CostUsdCents: number;
  PaymentCadenceDays: number;
  Name: string;
  CoverImageURL: string;
  Description: string;
  AccessGroupKeyName: string;
  // AccessGroupPublicKey is only required if the tier is E2E
  AccessGroupPublicKey?: string;
  E2E: boolean;
}
interface SubmitAtomicTransactionResponse {
  Transaction: MsgDeSoTxn;
  TxnHashHex: string;
  TransactionIDBase58Check: string;
}
export const createSubscriptionTier = async (
  payload: CreateSubscriptionTierRequest,
) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post<SubmitAtomicTransactionResponse>(
    "subscription/group",
    payload,
    getFocusAuthHeaders(jwt, payload.CreatorPublicKey),
  );
  return data;
};

interface UpdateSubscriptionTierRequest {
  CreatorPublicKey: string;
  AssociationId: string;
  Name: string;
  CoverImageURL: string;
  Description: string;
  DerivedPublicKey?: string;
}
interface UpdateSubscriptionTierResponse {
  associationTransaction: AssociationTxnResponse;
  associationResponse: AssociationResponse;
}
export const updateSubscriptionTier = async (
  payload: UpdateSubscriptionTierRequest,
) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.put<UpdateSubscriptionTierResponse>(
    "subscription/group",
    payload,
    getFocusAuthHeaders(jwt, payload.CreatorPublicKey),
  );
  return data;
};

interface DeleteSubscriptionTierRequest {
  CreatorPublicKey: string;
  AssociationId: string;
  DerivedPublicKey: string;
  ExpirationBlock: number;
  AccessSignature: string;
  TransactionSpendingLimitHex: string;
}
export const deleteSubscriptionTier = async (
  payload: DeleteSubscriptionTierRequest,
) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post<SubmitAtomicTransactionResponse>(
    "subscription/group/delete",
    payload,
    getFocusAuthHeaders(jwt, payload.CreatorPublicKey),
  );
  return data;
};

interface FocusAuthorizeDerivedKeyRequest {
  OwnerPublicKey: string;
  DerivedPublicKey: string;
  ExpirationBlock: number;
  AccessSignature: string;
  TransactionSpendingLimitHex: string;
  DeleteKey?: boolean;
}
interface FocusAuthorizeDerivedKeyResponse {
  AuthorizeDerivedKeyResponse: AuthorizeDerivedKeyResponse;
  AuthorizeDerivedKeyTransaction: SubmitTransactionResponse;
}
export const authorizeDerivedKey = async (
  payload: FocusAuthorizeDerivedKeyRequest,
) => {
  const { data } =
    await focusAPIInstance.post<FocusAuthorizeDerivedKeyResponse>(
      "derived-key/authorize",
      {
        ...payload,
        DeleteKey: payload.DeleteKey || false,
      },
      await getFocusAuthHeadersAsync(payload.OwnerPublicKey),
    );
  return data;
};

export const authorizeDerivedKeyAndWait = async (
  payload: FocusAuthorizeDerivedKeyRequest,
) => {
  const response = await authorizeDerivedKey(payload);
  const transactionHash = response.AuthorizeDerivedKeyTransaction.TxnHashHex;

  if (!transactionHash) {
    throw new Error(
      "Authorize derived key transaction was submitted but no transaction hash was returned.",
    );
  }

  await waitForTxn({
    transactionHash,
  });

  return response;
};

interface AddAccessGroupMemberRequest {
  AccessGroupOwnerPublicKey: string;
  AccessGroupKeyName: string;
  AccessGroupMemberPublicKey: string;
}
interface AddAccessGroupMemberResponse {
  TotalInputNanos: number;
  ChangeAmountNanos: number;
  FeeNanos: number;
  Transaction: MsgDeSoTxn;
  TransactionHex: string;
}
export const addAccessGroupMember = async (
  payload: AddAccessGroupMemberRequest,
) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post<AddAccessGroupMemberResponse>(
    "access-group-member",
    {
      ...payload,
    },
    getFocusAuthHeaders(jwt, payload.AccessGroupMemberPublicKey),
  );
  return data;
};

interface CreateSubscriptionRequest {
  SubscriberPublicKey: string;
  AccessGroupOwnerPublicKey: string;
  AccessGroupKeyName: string;
  MessageId?: string;
  PostHash?: string;
  CancelledSubscriptionAssociationId?: string;
}
interface CreateSubscriptionResponse {
  transaction: MsgDeSoTxn;
  txnHashHex: string;
  transactionIDBase58Check: string;
  accessGroups: {
    accessGroupKeyName: string;
    accessGroupOwnerPublicKey: string;
    encryptedKey: string;
  }[];
}
export const createSubscription = async (
  payload: CreateSubscriptionRequest,
): Promise<CreateSubscriptionResponse> => {
  const { data } = await focusAPIInstance.post(
    "subscription",
    {
      ...payload,
    },
    await getFocusAuthHeadersAsync(payload.SubscriberPublicKey),
  );
  return data;
};

interface DeleteSubscriptionRequest {
  AssociationId: string;
  DerivedPublicKey?: string;
  ExpirationBlock?: number;
  AccessSignature?: string;
  TransactionSpendingLimitHex?: string;
}
export const unsubscribe = async (payload: DeleteSubscriptionRequest) => {
  const [snapshot, jwt] = await Promise.all([
    identity.snapshot(),
    identity.jwt(),
  ]);

  if (!snapshot.currentUser) {
    throw new Error("Cannot unsubscribe without a logged in user.");
  }

  const { data } = await focusAPIInstance.post<SubmitAtomicTransactionResponse>(
    "subscription/delete",
    payload,
    getFocusAuthHeaders(jwt, snapshot.currentUser.publicKey),
  );
  return data;
};

export enum PollWeightType {
  unweighted = "unweighted",
  desoBalance = "deso_balance",
  desoTokenBalance = "deso_token_balance",
}

export interface UserRefCodeResponse {
  DeletedAt: string;
  PublicKeyBase58Check: string;
  ReferralCode: string;
  UpdatedAt: string;
  UpdatedBy: string;
}

export async function getReferralCode(publicKeyBase58Check: string) {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<UserRefCodeResponse>(
    `referral-codes/me`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export async function createReferralCode(publicKeyBase58Check: string) {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.post<UserRefCodeResponse>(
    `referral-codes`,
    {
      publicKeyBase58Check,
    },
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export async function getReferralCodeByCode(
  publicKeyBase58Check: string,
  code: string,
) {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<UserRefCodeResponse>(
    `referral-codes/${code}`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );

  return response.data;
}

export type TopEarnersResponse = {
  TopEarners: Array<{
    PublicKeyBase58Check: string;
    Earnings: number;
  }>;
};

export async function getEarningTopEarners(
  publicKeyBase58Check: string,
  params?: { category?: string; afterDaysAgo?: number; limit?: number },
): Promise<TopEarnersResponse | null> {
  const jwt = await identity.jwt();
  let url = "earnings/top-earners";
  if (params) {
    const { category, afterDaysAgo, limit } = params;
    const queryParams = [];
    if (category) queryParams.push(`category=${category}`);
    if (afterDaysAgo) queryParams.push(`afterDaysAgo=${afterDaysAgo}`);
    if (limit) queryParams.push(`limit=${limit}`);
    if (queryParams.length) url += `?${queryParams.join("&")}`;
  }

  const response = await focusAPIInstance.get<TopEarnersResponse>(
    url,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );

  return response.data;
}

export const getTotalEarningsRequest = async (publicKey: string) => {
  if (!publicKey) {
    return { EarningsUsdCents: 0 };
  }

  const { data } = await focusAPIInstance.get<{
    PublicKeyBase58Check: string;
    EarningsUsdCents: number;
  }>(`earnings/total/${publicKey}`);
  return data;
};

export type EarningByCategoryParams = {
  params: {
    payoutCategory?: string;
    payer?: string;
  };
};

export type EarningStatsResponse = {
  EarningsUsdCentsByCategory: Record<EarningEventCategory, number>;
  PublicKeyBase58Check: string;
};

export async function getEarningByCategory(
  referringPublicKeyBase58Check: string,
  referrerPublicKeyBase58Check: string,
  options?: EarningByCategoryParams,
): Promise<EarningStatsResponse | null> {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, referrerPublicKeyBase58Check);
  const response = await focusAPIInstance.get<EarningStatsResponse>(
    `earnings/by-category/${referringPublicKeyBase58Check}`,
    {
      ...options,
      ...headers,
    },
  );

  return response.data;
}

export async function getEarningByCategoryInternal(
  referringPublicKeyBase58Check: string,
  options?: EarningByCategoryParams,
): Promise<EarningStatsResponse | null> {
  const response = await focusInternalAPIInstance.get<EarningStatsResponse>(
    `earnings/by-category/${referringPublicKeyBase58Check}`,
    {
      ...options,
    },
  );

  return response.data;
}

export enum EarningEventCategory {
  NEW_SIGNUP = "NEW_SIGNUP",
  PAY_TO_MESSAGE = "PAY_TO_MESSAGE",
  PAY_TO_UNLOCK = "PAY_TO_UNLOCK",
  POST_TO_EARN = "POST_TO_EARN",
  REPOST_TO_EARN = "REPOST_TO_EARN",
  SUBSCRIPTION = "SUBSCRIPTION",
  TIP = "TIP",
  VIEW_TO_EARN = "VIEW_TO_EARN",
  TWITTER_BOUNTY_CLAIMED = "TWITTER_BOUNTY_CLAIMED",
}

export enum EarningPayoutCategory {
  BURNED_FEE = "BURNED_FEE",
  EARNING = "EARNING",
  INSTAGRAM_JOINING_REWARD = "INSTAGRAM_JOINING_REWARD",
  REFERRAL_REWARD = "REFERRAL_REWARD",
  REVENUE_SHARE = "REVENUE_SHARE",
  TWITTER_JOINING_REWARD = "TWITTER_JOINING_REWARD",
}

export type FocusProfileResponse = {
  Id: number;
  CreatedAt: string;
  PublicKeyBase58Check: string;
  ReferralCodeUsed: string;
  ReferrerPublicKeyBase58Check: string;
  Email: string;
  EmailIsVerified: boolean;
  Status: USERSTATUS;
};

export async function getFocusUser(publicKey: string) {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, publicKey);
  const response = await focusAPIInstance.get<FocusProfileResponse>(
    `/user/profile/${publicKey}`,
    {
      ...headers,
    },
  );
  return response;
}

type UpdateFocusUserPayload = {
  email: string;
};

export async function updateFocusUser(
  payload: UpdateFocusUserPayload,
  publicKey: string,
) {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.put(
    `/user/profile/me`,
    payload,
    getFocusAuthHeaders(jwt, publicKey),
  );
  return response;
}

interface GetTotalEarningsOptions {
  params: {
    eventCategory?: EarningEventCategory;
    payoutCategory?: EarningPayoutCategory;
    afterDaysAgo?: number;
  };
}
export interface GetTotalEarningsResponse {
  EarningsUsdCents: number;
  PublicKeyBase58Check: string;
}

export async function getTotalEarningsForUser(
  publicKey: string,
  options: GetTotalEarningsOptions,
) {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, publicKey);
  const response = await focusAPIInstance.get<GetTotalEarningsResponse>(
    `earnings/total/${publicKey}`,
    {
      ...options,
      ...headers,
    },
  );
  return response.data;
}
export interface CreateFocusProfileResponse {
  CreatedAt: string;
  PublicKeyBase58Check: string;
  ReferralCodeUsed: string;
  ReferrerPublicKeyBase58Check: string;
  Id: number;
}

export async function createFocusProfile(
  publicKeyBase58Check: string,
  code?: string,
) {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.post<CreateFocusProfileResponse>(
    `user/profile`,
    code
      ? {
          ReferralCode: code,
        }
      : {},
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export type TopBountiesDiscoveredResponse = {
  Count: number;
  Users: Array<TopBountiesDiscovered>;
};

type TopBountiesDiscoveredProps = {
  params: {
    limit?: number;
    offset?: number;
  };
};

export async function getTopBountiesDiscovered(
  options: TopBountiesDiscoveredProps,
): Promise<TopBountiesDiscoveredResponse | null> {
  const res = await focusAPIInstance.get<TopBountiesDiscoveredResponse>(
    `twitter/user/rewards/top`,
    {
      ...options,
    },
  );
  return res.data;
}

export type TopRewardsReferralResponse = {
  Count: number;
  Users: Array<TopReferrals>;
};

type TopRewardsReferralProps = {
  params: {
    limit?: number;
    offset?: number;
  };
};

export async function getTopRewardsReferral(
  options: TopRewardsReferralProps,
): Promise<TopRewardsReferralResponse | null> {
  const res = await focusAPIInstance.get<TopRewardsReferralResponse>(
    `rewards/referrals/top`,
    {
      ...options,
    },
  );
  return res.data;
}

export type RewardRecord = {
  CreatedAt: string;
  JoiningPublicKeyBase58Check: string;
  LockedJoiningRewardTxnHash: string;
  LockedJoiningRewardUSDCents: number;
  LockedReferralRewardTxnHash: string;
  LockedReferralRewardUSDCents: number;
  Platform: string;
  PostId: string;
  ReferralPublicKeyBase58Check: string;
  RejectionReason: string;
  Status: string;
  TotalJoiningRewardUsdCents: number;
  TotalReferralRewardUsdCents: number;
  UnlockedJoiningRewardTxnHash: string;
  UnlockedJoiningRewardUSDCents: number;
  UnlockedReferralRewardTxnHash: string;
  UnlockedReferralRewardUSDCents: number;
  UpdatedAt: string;
  UpdatedBy: string;
};

type TopReferral = {
  JoiningRewardUsdCents: number;
  JoinedFocusAt: string;
  PublicKeyBase58Check: string;
  ReferralRewardUsdCents: number;
  Username: string;
};

export type RewardsReferralResponse = {
  PublicKeyBase58Check: string;
  TopReferrals: Array<TopReferral>;
  TotalReferralsCount: number;
  TotalReferralsUsdCents: number;
  TotalRevenueShareUsdCents: number;
};

export async function getRewardsReferralInternal(
  userPublicKeyBase58Check: string,
): Promise<RewardsReferralResponse | null> {
  const response = await focusInternalAPIInstance.get<RewardsReferralResponse>(
    `rewards/referrals/for/${userPublicKeyBase58Check}`,
  );

  return response.data;
}

export type RewardsClaimsResponse = Array<RewardRecord>;

export interface RewardsClaimsOptions {
  params: {
    platform: keyof typeof SOCIALS_LIST;
  };
}

export async function getRewardsClaims(
  userPublicKeyBase58Check: string,
  options?: RewardsClaimsOptions,
): Promise<RewardsClaimsResponse | null> {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, userPublicKeyBase58Check);

  const response = await focusAPIInstance.get<RewardsClaimsResponse>(
    `rewards/claims/for/${userPublicKeyBase58Check}`,
    {
      ...options,
      ...headers,
    },
  );
  return response.data;
}

export type UserPublicSettingsResponse = {
  BountyHunterReferralRewardPercentageBasisPoints: number;
  BountyHunterRewardUnlockPercentage: number;
  ReferralCodeDefaultLength: number;
  TwitterJoinToEarnTweetText: string;
  ReferralUserFeeBasisPoints: number;
  FocusUserFeeBasisPoints: number;
};

export async function getUserPublicSettings(
  userPublicKeyBase58Check: string,
): Promise<UserPublicSettingsResponse | null> {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, userPublicKeyBase58Check);

  const response = await focusAPIInstance.get<UserPublicSettingsResponse>(
    `/settings/public`,
    {
      ...headers,
    },
  );
  return response.data;
}

export async function getRewardsClaimsInternal(
  userPublicKeyBase58Check: string,
  options?: RewardsClaimsOptions,
): Promise<RewardsClaimsResponse | null> {
  const response = await focusInternalAPIInstance.get<RewardsClaimsResponse>(
    `rewards/claims/for/${userPublicKeyBase58Check}`,
    {
      ...options,
    },
  );
  return response.data;
}

export type UserProfileInternalResponse = {
  CreatedAt: string;
  Id: number;
  PublicKeyBase58Check: string;
  ReferralCodeUsed: string;
  ReferrerPublicKeyBase58Check: string;
};

export async function getFocusUserInternal(
  refererrerPublicKeyBase58Check: string,
  publicKey: string,
): Promise<UserProfileInternalResponse> {
  const response =
    await focusInternalAPIInstance.get<UserProfileInternalResponse>(
      `user/profile/${refererrerPublicKeyBase58Check}`,
      {
        headers: {
          "Public-Key": publicKey,
        },
      },
    );

  return response.data;
}

type TwitterUserGraphOptionsType = {
  limit?: number;
  username: string;
};

export type TwitterUserGraphInteraction = {
  InteractionType: string;
  Tweet: {
    AuthorId: string;
    BookmarkCount: number;
    CreatedAt: string;
    ImpressionCount: number;
    LikeCount: number;
    QuoteCount: number;
    ReplyCount: number;
    RetweetCount: number;
    Text: string;
    TweetId: string;
    UpdatedAt: string;
    Version: string;
  };
  InteractorUser: {
    BountyHunterAlgoVersion: number;
    CreatedAt: string;
    FollowersCount: number;
    FollowingCount: number;
    JoiningRewardUsdCents: number;
    ListedCount: number;
    Name: string;
    ProfileImageUrl: string;
    ReferralRewardUsdCents: number;
    TweetCount: number;
    UpdatedAt: string;
    UserId: string;
    Username: string;
    Verified: boolean;
    VerifiedType: string;
    Version: string;
    ViewCount: number;
  };
};

export enum VerifiedType {
  Blue = "blue",
  Gold = "gold",
  Business = "business",
  None = "none",
}

export type TwitterUserGraphResponse = {
  Error: string;
  Interactions: TwitterUserGraphInteraction[];
  Status: string;
  UpdatedAt: string;
  User: {
    BountyHunterAlgoVersion: number;
    CreatedAt: string;
    FollowersCount: number;
    FollowingCount: number;
    JoiningRewardUsdCents: number;
    ListedCount: number;
    Name: string;
    ProfileImageUrl: string;
    ReferralRewardUsdCents: number;
    TweetCount: number;
    UpdatedAt: string;
    UserId: string;
    Username: string;
    Verified: true;
    VerifiedType: VerifiedType;
    Version: string;
    ViewCount: number;
  };
};

// export async function getTotalEarningsForUser(
//   publicKey: string,
//   options: GetTotalEarningsOptions,
// ) {
//   const jwt = await identity.jwt();
//   const headers = getFocusAuthHeaders(jwt, publicKey);
//   const response = await focusAPIInstance.get(`earnings/total/${publicKey}`, {
//     ...options,
//     ...headers,
//   });
//   return response.data as GetTotalEarningsResponse;
// }

export async function getTwitterUserGraphAsync(
  userPublicKeyBase58Check: string,
  options?: TwitterUserGraphOptionsType,
) {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, userPublicKeyBase58Check);

  const response = await focusAPIInstance.get<TwitterUserGraphResponse>(
    `twitter/user-graph-async?username=${options?.username}&limit=${options?.limit || 100}`,
    {
      ...headers,
      ...options,
    },
  );

  return response.data;
}

export async function getTwitterUserGraphProgress(
  publicKey: string,
  options?: TwitterUserGraphOptionsType,
) {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, publicKey);

  const response = await focusAPIInstance.get<TwitterUserGraphResponse>(
    `twitter/user-graph-progress?username=${options?.username}&limit=${options?.limit || 100}`,
    {
      ...headers,
      ...options,
    },
  );

  return response.data;
}

export async function getTwitterUserGraphProgressInternal(
  publicKey: string,
  jwt: string,
  options?: TwitterUserGraphOptionsType,
) {
  const response = await focusInternalAPIInstance.get(
    `twitter/user-graph-progress?username=${options?.username}&limit=${options?.limit || 100}`,
    getFocusAuthHeaders(jwt, publicKey),
  );
  return response.data;
}

export type TopBountiesDiscovered = {
  TwitterUsername: string;
  TwitterProfileImageUrl: string;
  JoiningRewardUsdCents: number;
  ReferralRewardUsdCents: number;
  PageViews: number;
};

export type TopReferrals = {
  PublicKeyBase58Check: string;
  TotalEarningsUsdCents: number;
  TotalReferralsUsdCents: number;
  TotalRevenueShareUsdCents: number;
  Username: string;
};

export interface TotalEarningsOptions {
  params: {
    eventCategory?: EarningEventCategory;
    payoutCategory?: EarningPayoutCategory;
    afterDaysAgo?: number;
    payer?: string;
  };
}

export async function getTotalEarnings(
  publicKey: string,
  userPublicKey: string,
  options?: TotalEarningsOptions,
) {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, userPublicKey);
  const response = await focusAPIInstance.get<GetTotalEarningsResponse>(
    `earnings/total/${publicKey}`,
    {
      ...options,
      ...headers,
    },
  );
  return response.data;
}

export async function getTotalEarningsInternal(
  publicKey: string,
  options?: TotalEarningsOptions,
) {
  const response = await focusInternalAPIInstance.get<GetTotalEarningsResponse>(
    `earnings/total/${publicKey}`,
    {
      ...options,
    },
  );
  return response.data;
}

export const setNotificationsSettings = async (
  publicKey: string,
  payload: NotificationSettings,
) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIV1Instance.post(
    `notifications/settings`,
    payload,
    getFocusAuthHeaders(jwt, publicKey),
  );
  return data;
};

export enum NotificationSettingFilterType {
  ENABLED = "ENABLED",
  DISABLED = "DISABLED",
  CUSTOM = "CUSTOM",
  WHALE_ONLY = "WHALE_ONLY",
}

export const NOTIFICATION_SETTING_FILTER_TYPE_LABELS = {
  [NotificationSettingFilterType.ENABLED]: "Enabled",
  [NotificationSettingFilterType.DISABLED]: "Disabled",
  [NotificationSettingFilterType.CUSTOM]: "Custom",
  [NotificationSettingFilterType.WHALE_ONLY]: "Whale Only",
};

type NotificationSettingItemFilterData = {
  FilterType: NotificationSettingFilterType;
  MinAmountUsdCents: number;
  MinActorWealthUsdCents: number;
};

export type NotificationSettings = {
  TransmissionTypes: {
    DAILY_DIGEST: "CUSTOM";
    EMAIL: "CUSTOM";
    PUSH: "CUSTOM";
    WEEKLY_DIGEST: "CUSTOM";
  };
  WhaleThresholdUsdCents: number;

  // NOTE: keys are defined explicitly to ensure all needed subcategories are covered
  FilterType: {
    GLOBAL: {
      [NotificationSubCategory.HIGH_VALUE_JOINING_REWARD]: NotificationSettingItemFilterData;
      NEW_WHALE_COIN: NotificationSettingItemFilterData;
    };
    [NotificationCategory.EARNINGS]: {
      [NotificationSubCategory.BOUNTY_REWARD_CLAIMED]: NotificationSettingItemFilterData;
      [NotificationSubCategory.COIN_LOCKUP_YIELD]: NotificationSettingItemFilterData;
      [NotificationSubCategory.COIN_TRADING_FEE]: NotificationSettingItemFilterData;
      [NotificationSubCategory.NEW_SUBSCRIPTION]: NotificationSettingItemFilterData;
      [NotificationSubCategory.PAID_MESSAGE_REDEMPTION]: NotificationSettingItemFilterData;
      [NotificationSubCategory.POST_TIP]: NotificationSettingItemFilterData;
      [NotificationSubCategory.RECEIVED_DESO]: NotificationSettingItemFilterData;
      [NotificationSubCategory.RECEIVED_DUSDC]: NotificationSettingItemFilterData;
      [NotificationSubCategory.RECEIVED_FOCUS]: NotificationSettingItemFilterData;
      [NotificationSubCategory.RECEIVED_POST_TO_EARN]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REFERRAL_EARNING]: NotificationSettingItemFilterData;
      [NotificationSubCategory.UNLOCKABLE_CONTENT]: NotificationSettingItemFilterData;
    };

    [NotificationCategory.MESSAGES]: {
      [NotificationSubCategory.ADDED_TO_GROUP_CHAT]: NotificationSettingItemFilterData;
      [NotificationSubCategory.NEW_DIRECT_PAID_MESSAGE]: NotificationSettingItemFilterData;
      [NotificationSubCategory.NEW_DIRECT_PAID_MESSAGE_REPLY]: NotificationSettingItemFilterData;
      [NotificationSubCategory.NEW_DIRECT_UNPAID_MESSAGE]: NotificationSettingItemFilterData;
      [NotificationSubCategory.NEW_GROUP_UNPAID_MESSAGE]: NotificationSettingItemFilterData;
    };
    [NotificationCategory.SOCIAL]: {
      [NotificationSubCategory.ADDED_TO_LIST]: NotificationSettingItemFilterData;
      [NotificationSubCategory.FOLLOW]: NotificationSettingItemFilterData;
      [NotificationSubCategory.FOLLOWEE_NEW_POST]: NotificationSettingItemFilterData;
      [NotificationSubCategory.MENTION]: NotificationSettingItemFilterData;
      [NotificationSubCategory.POLL_RESPONSE]: NotificationSettingItemFilterData;
      [NotificationSubCategory.POST_QUOTE_REPOST]: NotificationSettingItemFilterData;
      [NotificationSubCategory.POST_REPLY]: NotificationSettingItemFilterData;
      [NotificationSubCategory.POST_REPOST]: NotificationSettingItemFilterData;
      [NotificationSubCategory.POST_VIEW_THRESHOLD]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REACTION_ANGRY]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REACTION_ASTONISHED]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REACTION_DISLIKE]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REACTION_LAUGH]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REACTION_LIKE]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REACTION_LOVE]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REACTION_SAD]: NotificationSettingItemFilterData;
      [NotificationSubCategory.REFERRAL_JOINED_FOCUS]: NotificationSettingItemFilterData;
      [NotificationSubCategory.WHALE_POST_VIEW]: NotificationSettingItemFilterData;
    };
    TRADING: {
      [NotificationSubCategory.COIN_PURCHASE_FILLED]: NotificationSettingItemFilterData;
      [NotificationSubCategory.COIN_PURCHASE_PLACED]: NotificationSettingItemFilterData;
      [NotificationSubCategory.COIN_SALE_FILLED]: NotificationSettingItemFilterData;
      [NotificationSubCategory.COIN_SALE_PLACED]: NotificationSettingItemFilterData;
    };
  };
};

export const getNotificationSettings = async (publicKey: string) => {
  if (!publicKey) {
    return null;
  }

  const jwt = await identity.jwt();

  if (!jwt) {
    return null;
  }

  const { data } = await focusAPIV1Instance.get<NotificationSettings>(
    `notifications/settings/me`,
    getFocusAuthHeaders(jwt, publicKey),
  );

  return data || null;
};

export const getPreviewNotificationsRequest = async ({
  publicKey,
  offset,
  limit,
}: {
  publicKey: string;
  offset: number;
  limit: number;
}) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIV1Instance.get<NotificationRequestResponse>(
    `notifications/settings/preview`,
    {
      headers: getFocusAuthHeaders(jwt, publicKey).headers,
      params: {
        offset,
        limit,
      },
    },
  );
  return data || [];
};

export type FocusNotification = {
  Notification: {
    ActorPublicKeyBase58Check: string;
    Category: NotificationCategory;
    CreatedAt: string;
    Id: string;
    Status: NotificationStatus;
    Subcategory: NotificationSubCategory;
    UpdatedAt: string;
    UserPublicKeyBase58Check: string;
    ExtraData: Record<string, string> & {
      ReferralNotificationSubcategory?: ReferralNotificationSubcategory;
    };
    // Optional fields based on subcategory type
    AmountUsdCents?: number;
    AmountNanosHex?: string;
    PostHashHex?: string;
    PostViewCount?: number;
    Quantity?: number;
    PriceUsd?: number;
    QuantityBought?: number;
    QuantitySold?: number;
    PollResponse?: string;
    Post?: {
      Body?: string;
      ImageUrls?: string[];
    };
  };
  ActorUser: {
    PublicKey: string;
    Username: string;
    DisplayName: string;
    TotalWealthUsdCents: number;
    ExtraData?: Record<string, string>;
  };
  User: {
    PublicKey: string;
    Username: string;
    DisplayName: string;
    TotalWealthUsdCents: number;
  };
};

type CreateDiamondsPayload = {
  SenderPublicKey: string;
  DiamondLevel: number;
};

type CreatePostDiamondsPayload = CreateDiamondsPayload & {
  PostHashHex: string;
};

type CreateUserDiamondsPayload = CreateDiamondsPayload & {
  RecipientPublicKey: string;
};

type CreateDiamondsResponse = {
  Transaction: MsgDeSoTxn;
  TxnHashHex: string;
  TransactionIDBase58Check: string;
};

export const sendPostDiamonds = async (
  payload: CreatePostDiamondsPayload,
): Promise<CreateDiamondsResponse> => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `diamond/post`,
    payload,
    getFocusAuthHeaders(jwt, payload.SenderPublicKey),
  );
  return data;
};

export const sendUserDiamonds = async (
  payload: CreateUserDiamondsPayload,
): Promise<CreateDiamondsResponse> => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `diamond/user`,
    payload,
    getFocusAuthHeaders(jwt, payload.SenderPublicKey),
  );
  return data;
};

export type UserSocialProfilesResponse = UserSocialProfiles;

export async function getUserSocialProfiles(publicKey: string) {
  const response = await focusAPIInstance.get<UserSocialProfilesResponse>(
    `/user/social-profiles/${publicKey}`,
  );
  return response.data;
}

export async function getUserSocialProfilesInternal(
  publicKey: string,
): Promise<UserSocialProfilesResponse> {
  const response =
    await focusInternalAPIInstance.get<UserSocialProfilesResponse>(
      `user/social-profiles/${publicKey}`,
    );

  return response.data;
}

export interface TopBountyOptions {
  userId: string;
  params?: {
    platform: keyof typeof SOCIALS_LIST;
    limit: number;
  };
}

export type TopBountyRecord = {
  BountyUserId: string;
  RequesterUsername: string;
  RequesterWealthUsdCents: number;
  CreatedAt: string;
  Id: string;
  Platform: string;
  RequesterPublicKeyBase58Check: string;
  Status: string;
  SuggestedBountyUsdCents: number;
};

export async function getTopBounty({ userId, params }: TopBountyOptions) {
  const res = await focusAPIInstance.get<TopBountyRecord[]>(
    `rewards/suggestions/${userId}`,
    {
      params,
    },
  );

  return res.data;
}

interface SendPaidMessageRequest {
  // The public key of the user sending the message.
  SenderPublicKey: string;
  // The public key of the user receiving the message.
  RecipientPublicKey: string;
  // The sender's access group public key.
  SenderAccessGroupPublicKeyBase58Check: string;
  // The recipient's access group public key.
  RecipientAccessGroupPublicKeyBase58Check: string;
  // The amount of USD cents to send with the message.
  PaymentAmountUsdCents: number;
  // The encrypted message hex to send.
  EncryptedTextHex: string;
  // Arbitrary extra data to send with the message.
  ExtraData: { [key: string]: string };
}

export const sendPaidMessage = async (payload: SendPaidMessageRequest) => {
  const res = await focusAPIInstance.post<SubmitAtomicTransactionResponse>(
    `message/send`,
    payload,
    await getFocusAuthHeadersAsync(),
  );

  return res.data;
};

interface PayMessageRequest {
  MessageBadgerKey: string;
}

export const sendMessagePayment = async (payload: PayMessageRequest) => {
  const res = await focusAPIInstance.post<SubmitAtomicTransactionResponse>(
    `message/payment`,
    payload,
    await getFocusAuthHeadersAsync(),
  );

  return res.data;
};

type BountyRequestDTO = {
  bountyUsdCents: number;
  platform: keyof typeof SOCIALS_LIST;
  userId: string;
  userPublicKeyBase58Check: string;
};

export const submitBountyRequest = async ({
  userPublicKeyBase58Check,
  ...params
}: BountyRequestDTO) => {
  const jwt = await identity.jwt();
  const res = await focusAPIInstance.post(
    `rewards/suggestions`,
    {
      ...params,
    },
    getFocusAuthHeaders(jwt, userPublicKeyBase58Check),
  );

  return res.data;
};

export async function getUserOnboardingSession(publicKey: string) {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, publicKey);

  const response = await focusAPIInstance.get(`user/onboarding-session`, {
    ...headers,
  });

  let LastStepUrl = response.data.LastStepUrl;
  if (LastStepUrl === "/sign-up/buy-focus") {
    LastStepUrl = APP_ROUTES.app;
  }

  return {
    ...response.data,
    LastStepUrl,
  };
}

type UserOnboardingSessionPayload = {
  ExtraData: string;
  LastStepUrl: string;
};

export const setUserOnboardingSession = async (
  publicKey: string,
  payload: UserOnboardingSessionPayload,
) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `user/onboarding-session`,
    payload,
    getFocusAuthHeaders(jwt, publicKey),
  );
  return data;
};

type CreatePostModerationPayload = {
  PostHashHex: string;
  ModerationTag: "NSFW" | "HIDE_FROM_FOR_YOU" | "REPORT";
  ModerationReason: string;
};

export const createPostModeration = async (
  payload: CreatePostModerationPayload,
) => {
  const { data } = await focusAPIInstance.post(
    `moderations/post/create`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
  return data;
};

type DeletePostModerationPayload = {
  PostHashHex: string;
  ModerationTag: "NSFW" | "HIDE_FROM_FOR_YOU" | "REPORT";
};

export const deletePostModeration = async (
  payload: DeletePostModerationPayload,
) => {
  const { data } = await focusAPIInstance.post(
    `moderations/post/delete`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
  return data;
};

type CreateUserModerationPayload = {
  PublicKeyBase58Check: string;
  ModerationTag:
    | "GREYLIST"
    | "BLACKLIST"
    | "NSFW"
    | "HIDE_FROM_FOR_YOU"
    | "REPORT";
  ModerationReason: string;
};

export const createUserModeration = async (
  payload: CreateUserModerationPayload,
) => {
  const { data } = await focusAPIInstance.post(
    `moderations/user/create`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
  return data;
};

type DeleteUserModerationPayload = {
  PublicKeyBase58Check: string;
  ModerationTag:
    | "GREYLIST"
    | "BLACKLIST"
    | "NSFW"
    | "HIDE_FROM_FOR_YOU"
    | "REPORT"
    | "BOT"
    | "SPAM"
    | "NFT";
};

export const deleteUserModeration = async (
  payload: DeleteUserModerationPayload,
) => {
  const { data } = await focusAPIInstance.post(
    `moderations/user/delete`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
  return data;
};

export async function deleteUserOnboardingSession(publicKey: string) {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, publicKey);

  const response = await focusAPIInstance.delete(`user/onboarding-session`, {
    ...headers,
  });
  return response.data;
}
type GetFeedPostIDListParams = {
  publicKey: string;
  limit: number;
  offset: number;
  hideViewed?: boolean;
  hideNSFW?: boolean;
  useModeration?: boolean;
};

export type GetFeedPostIDListResponse = {
  postHashes: string[];
  publicKey: string;
  feedProviderPublicKey: string;
  feedId: string;
  feedName: string;
};

export const getFeedPostIdList = async ({
  publicKey,
  limit,
  offset,
  hideViewed = true,
  hideNSFW = false,
  useModeration = true,
}: GetFeedPostIDListParams) => {
  if (!publicKey) {
    throw new Error("Can not request feed post id list without public key");
  }

  const authHeaders = await getOptionalFocusAuthHeadersAsync(publicKey);

  const { data } = await focusAPIInstance.get<GetFeedPostIDListResponse>(
    `my-feed`,
    {
      headers: authHeaders.headers,
      params: {
        limit,
        offset,
        publicKey,
        hide_viewed:
          process.env.NEXT_PUBLIC_UI_ENABLE_HIDE_VIEWED_POSTS === "false"
            ? "false"
            : hideViewed.toString(),
        hide_nsfw: hideNSFW.toString(),
        use_moderation: useModeration.toString(),
      },
    },
  );
  return data;
};

export enum FeedQueryParamType {
  BOOL = "BOOL",
  FLOAT = "FLOAT",
  INT = "INT",
  STRING = "STRING",
}

export enum FeedQueryParamConstraintType {
  MIN = "MIN",
  MAX = "MAX",
}

export const FEED_QUERY_PARAM_CONSTRAINTS = {
  [FeedQueryParamType.BOOL]: [],
  [FeedQueryParamType.FLOAT]: [
    FeedQueryParamConstraintType.MIN,
    FeedQueryParamConstraintType.MAX,
  ],
  [FeedQueryParamType.INT]: [
    FeedQueryParamConstraintType.MIN,
    FeedQueryParamConstraintType.MAX,
  ],
  [FeedQueryParamType.STRING]: [],
};

export const FeedQueryParamTypeLabels = {
  [FeedQueryParamType.BOOL]: "Boolean",
  [FeedQueryParamType.FLOAT]: "Float",
  [FeedQueryParamType.INT]: "Integer",
  [FeedQueryParamType.STRING]: "String",
};

export const FeedQueryParamConstraintTypeLabels = {
  [FeedQueryParamConstraintType.MIN]: "Minimum",
  [FeedQueryParamConstraintType.MAX]: "Maximum",
};

export type FeedQueryParamRecordConstraint = {
  type: FeedQueryParamConstraintType;
  value: string;
};

export type FeedQueryParamRecord = {
  name: string;
  id: string;
  description: string;
  type: FeedQueryParamType;
  defaultValue: string;
  Group: string;
  IsAdvanced: boolean;
  constraints: Array<FeedQueryParamRecordConstraint> | null;
};

export type FeedConfig = {
  isFree: boolean;
  publicKey: string;
  feedUrl: string;
  feedName: string;
  feedId: string;
  feedDescription: string;
  settingParams: Record<string, FeedQueryParamRecord>;
};

export const createFeed = async (payload: FeedConfig, publicKey: string) => {
  const jwt = await identity.jwt();
  if (!publicKey) {
    throw new Error("No logged in user.");
  }

  await focusAPIInstance.put(
    `feed-marketplace/feed`,
    payload,
    getFocusAuthHeaders(jwt, publicKey),
  );
};

export type UserFeedSettingsPayload = {
  publicKey: string;
  feedPublicKey: string;
  feedId: string;
  feedParams: Dictionary<string>;
};

export const subscribeUserToFeed = async (
  payload: Omit<UserFeedSettingsPayload, "feedParams">,
) => {
  return await focusAPIInstance.post(
    `my-feed/subscribe`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
};

export const updateFeedSettings = async (
  payload: UserFeedSettingsPayload,
  publicKey: string,
) => {
  const jwt = await identity.jwt();
  if (!publicKey) {
    throw new Error("No logged in user.");
  }

  const response = await subscribeUserToFeed({
    publicKey: payload.publicKey,
    feedPublicKey: payload.feedPublicKey,
    feedId: payload.feedId,
  });

  if (response?.data?.associationTransaction?.TxnHashHex) {
    await waitForTxn({
      transactionHash: response.data.associationTransaction.TxnHashHex,
    });
  }

  await focusAPIInstance.put(`my-feed/settings`, payload, {
    headers: getFocusAuthHeaders(jwt, publicKey).headers,
  });
};

export type FeedSettingsResponse = {
  FeedId: string;
  FeedName: string;
  IsFree: string;
  FeedURL: string;
  FeedVersion: string;
  FeedDescription: string;
  DerivedPublicKey: string;
  FeedQueryParams: Record<string, FeedQueryParamRecord>;
  FeedSettingParams: Record<string, FeedQueryParamRecord>;
};

export interface PutPaidMessagingSettingsRequest {
  FeePerMessageUsdCents: number;
  FollowingFeePerMessageUsdCents: number;
  UserPublicKeyBase58: string;
  Version: number;
}
export const updatePaidMessagingSettings = async (
  payload: PutPaidMessagingSettingsRequest,
) => {
  await focusAPIInstance.put(
    `settings/user/paid-messages`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
};

export type ListMembersPayload = {
  listId: string;
  userPublicKeyBase58Check: string;
  userPublicKeysBase58Check: string[];
};

export type ListEntityMember = {
  CreatedAt?: string;
  List?: string;
  ListId: string;
  ListMemberPublicKeyBase58Check: string;
};

export type ListEntity = {
  CreatedAt: string;
  ListDescription: string;
  ListId: string;
  ListName: string;
  /**
   * @deprecated This property is deprecated and will be removed in future versions.
   */
  Members: ListEntityMember[] | null;
  MembersLimit: number;
  MembersCount: number;
  UpdatedAt: string;
  UserPublicKeyBase58Check: string;
};

export type ListMember = {
  ListMemberPublicKeyBase58Check: string;
  ListId: string;
  List: null | string;
  CreatedAt: string;
};

export type CreateUpdateListPayload = {
  // TODO check if we need this - swagger is not updated
  listId?: string;
  listDescription?: string;
  listName: string;
  membersLimit: number;
  userPublicKeyBase58Check: string;
};

export const addMemberToList = async (payload: ListMembersPayload) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `lists/add-members-to-list`,
    payload,
    getFocusAuthHeaders(jwt, payload.userPublicKeyBase58Check),
  );
  return data;
};

export const clearList = async (listId: string, publicKey: string) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `lists/clear-list/${listId}`,
    {},
    getFocusAuthHeaders(jwt, publicKey),
  );
  return data;
};

export const deleteList = async (listId: string, publicKey: string) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `lists/delete-list/${listId}`,
    {},
    getFocusAuthHeaders(jwt, publicKey),
  );
  return data;
};

export const getUserListsRequest = async (publicKey: string) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get<{
    Lists: ListEntity[];
    TotalCount: number;
  }>(`lists/for/${publicKey}`, getFocusAuthHeaders(jwt, publicKey));
  return data ?? [];
};

export const createUserAccountsList = async (
  payload: CreateUpdateListPayload,
): Promise<ListEntity> => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.put(
    `lists/list`,
    payload,
    getFocusAuthHeaders(jwt, payload.userPublicKeyBase58Check),
  );

  return data;
};

export const updateUserAccountsList = async (
  payload: CreateUpdateListPayload,
): Promise<ListEntity> => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.patch(
    `lists/list`,
    payload,
    getFocusAuthHeaders(jwt, payload.userPublicKeyBase58Check),
  );
  return data;
};

type GetListMembersDTO = {
  listId: string;
  publicKey: string;
  limit: number;
  offset: number;
};

export type ListMemberResponse = {
  Members: ListMember[];
  TotalCount: number;
};

export const getUserAccountListMembers = async ({
  listId,
  publicKey,
  limit,
  offset,
}: GetListMembersDTO) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get<ListMemberResponse>(
    `lists/list-members/${listId}`,
    {
      headers: getFocusAuthHeaders(jwt, publicKey).headers,
      params: {
        limit,
        offset,
      },
    },
  );

  return data;
};

type UserAssociatedListDTO = {
  ownerPublicKey: string;
  userPublicKey: string;
};

export const getUserAssociatedLists = async ({
  ownerPublicKey,
  userPublicKey,
}: UserAssociatedListDTO) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get<ListEntity[]>(
    `/lists/for/${ownerPublicKey}/user/${userPublicKey}`,
    getFocusAuthHeaders(jwt, ownerPublicKey),
  );
  return data ?? [];
};

export const removeMembersFromList = async (payload: ListMembersPayload) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `lists/remove-members-from-list`,
    payload,
    getFocusAuthHeaders(jwt, payload.userPublicKeyBase58Check),
  );
  return data;
};

interface CreateMessageViewRequest {
  ThreadIdentifier: string;
}
interface CreateMessageViewsRequest {
  MessageViews: CreateMessageViewRequest[];
}

export const createMessageViews = async (
  payload: CreateMessageViewsRequest,
): Promise<void> => {
  return focusAPIInstance.post(
    `message/views`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
};

export enum REFERRAL_CODE_STATUS {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export type ReferralCode = {
  referralCode: string;
  referralsUsed: number;
  referralsAllowed: number;
  status: REFERRAL_CODE_STATUS;
};

export type GetReferralCodesResponse = {
  totalReferralsUsed: number;
  totalReferralsAllowed: number;
  referralCodes: ReferralCode[];
};

export const getReferralCodes = async (): Promise<GetReferralCodesResponse> => {
  const response = await focusAPIInstance.get<GetReferralCodesResponse>(
    `/referral-codes/all`,
    await getFocusAuthHeadersAsync(),
  );
  return response.data;
};

export type ReferralCodeUser = {
  publicKeyBase58Check: string;
  username: string;
  displayName: string;
  profileImageUrl: string;
  revenueShareEarningsUsdCents: number;
  extraData: Record<string, string>;
};

export type GetReferralCodeUsersResponse = {
  users: ReferralCodeUser[];
};

export const getReferralCodeUsers = async (
  referralCode: string,
): Promise<GetReferralCodeUsersResponse> => {
  const response = await focusAPIInstance.get<GetReferralCodeUsersResponse>(
    `/referral-codes/${referralCode}/users`,
    await getFocusAuthHeadersAsync(),
  );
  return response.data;
};

export const activateReferralCode = async (referralCode: string) => {
  await focusAPIInstance.post(
    `/referral-codes/${referralCode}`,
    {},
    await getFocusAuthHeadersAsync(),
  );
};

export const disableReferralCode = async (referralCode: string) => {
  await focusAPIInstance.delete(
    `/referral-codes/${referralCode}`,
    await getFocusAuthHeadersAsync(),
  );
};

interface CreateReferralCodeRequest {
  MaxUsesCount: number; // 0 for infinite
}

export const createMyReferralCode = async (
  maxUsesCount: number,
): Promise<void> => {
  const requestBody: CreateReferralCodeRequest = {
    MaxUsesCount: maxUsesCount,
  };

  await focusAPIInstance.post(
    `/referral-codes`,
    requestBody,
    await getFocusAuthHeadersAsync(),
  );
};

export interface UserSettingsResponse {
  user: {
    publicKeyBase58Check: string;
    username: string;
    displayName: string;
    profilePicUrl: string;
  };
  settings: {
    maxReferralsCount: number;
  };
  updatedBy: {
    publicKeyBase58Check: string;
    username: string;
    displayName: string;
    profilePicUrl: string;
  };
  updatedAt: string;
}

export async function getUserSettings(
  publicKeyBase58Check: string,
  currentUserPublicKey: string,
) {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get<UserSettingsResponse>(
    `user/settings/${publicKeyBase58Check}`,
    getFocusAuthHeaders(jwt, currentUserPublicKey),
  );
  return data;
}

export async function saveUserSettings(
  publicKeyBase58Check: string,
  currentUserPublicKey: string,
  payload: { maxReferralsCount: number },
) {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post(
    `user/settings/${publicKeyBase58Check}`,
    payload,
    getFocusAuthHeaders(jwt, currentUserPublicKey),
  );
  return data;
}

export async function resetUserSettings(
  publicKeyBase58Check: string,
  currentUserPublicKey: string,
) {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.delete(
    `user/settings/${publicKeyBase58Check}`,
    getFocusAuthHeaders(jwt, currentUserPublicKey),
  );
  return data;
}

export const pinPost = async (
  publicKey: string,
  postHashHex: string,
): Promise<void> => {
  const jwt = await identity.jwt();
  await focusAPIInstance.post(
    "/post/pin",
    { PostHashHex: postHashHex },
    getFocusAuthHeaders(jwt, publicKey),
  );
};

export const unpinPost = async (
  publicKey: string,
  postHashHex: string,
): Promise<void> => {
  const jwt = await identity.jwt();
  await focusAPIInstance.delete(
    `/post/pin/${postHashHex}`,
    getFocusAuthHeaders(jwt, publicKey),
  );
};

export const enum TransactionOperationType {
  BID = "BID",
  ASK = "ASK",
}

export type TradesTransaction = {
  DenominatedCoinPublicKeyBase58Check: string;
  OperationType: TransactionOperationType;
  PriceDenominatedCoin: number;
  PriceUsd: number;
  Quantity: number;
  TargetCoinPublicKeyBase58Check: string;
  Timestamp: string;
  TxnHashHex: string;
  UserPublicKeyBase58Check: string;
  ValueDenominatedCoin: number;
  ValueUsd: number;
  OrderType: string;
};

export type TradesTransactionsResponse = TradesTransaction[];

export async function getTradesTransactions({
  coinPublicKey,
  currentUserPublicKey,
  limit = 10,
}: {
  coinPublicKey: string;
  currentUserPublicKey: string;
  limit?: number;
}) {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get<TradesTransactionsResponse>(
    `transactions/recent/trades/${coinPublicKey}`,
    {
      ...getFocusAuthHeaders(jwt, currentUserPublicKey),
      params: {
        limit,
      },
    },
  );
  return data;
}

export type TransfersTransaction = {
  AmountNanosHex: string;
  CoinPublicKeyBase58Check: string;
  ReceiverDisplayName: string;
  ReceiverProfilePictureUrl: string;
  ReceiverPublicKeyBase58Check: string;
  ReceiverUsername: string;
  SenderPublicKeyBase58Check: string;
  Timestamp: string;
  TxnHashHex: string;
};

export type TransfersTransactionsResponse = TransfersTransaction[];

export async function getTransfersTransactions({
  coinPublicKey,
  currentUserPublicKey,
  limit = 10,
}: {
  coinPublicKey: string;
  currentUserPublicKey: string;
  limit?: number;
}) {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get<TransfersTransactionsResponse>(
    `transactions/recent/transfers/${coinPublicKey}`,
    {
      ...getFocusAuthHeaders(jwt, currentUserPublicKey),
      params: {
        limit,
      },
    },
  );
  return data;
}

export type BurnTransaction = {
  AmountNanosHex: string;
  CoinPublicKeyBase58Check: string;
  UserPublicKeyBase58Check: string;
  Timestamp: string;
  TxnHashHex: string;
};

export type BurnTransactionsResponse = BurnTransaction[];

export async function getBurnTransactions({
  coinPublicKey,
  currentUserPublicKey,
  limit = 10,
}: {
  coinPublicKey: string;
  currentUserPublicKey: string;
  limit?: number;
}) {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get<BurnTransactionsResponse>(
    `transactions/recent/burns/${coinPublicKey}`,
    {
      ...getFocusAuthHeaders(jwt, currentUserPublicKey),
      params: {
        limit,
      },
    },
  );
  return data;
}

export type LockupTransaction = {
  AmountNanosHex: string;
  CoinPublicKeyBase58Check: string;
  UserPublicKeyBase58Check: string;
  Timestamp: string;
  TxnHashHex: string;
  UnlockDate: string;
};

export type LockupTransactionsResponse = LockupTransaction[];

export async function getLockupTransactions({
  coinPublicKey,
  currentUserPublicKey,
  limit = 10,
}: {
  coinPublicKey: string;
  currentUserPublicKey: string;
  limit?: number;
}) {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get<LockupTransactionsResponse>(
    `transactions/recent/lockups/${coinPublicKey}`,
    {
      ...getFocusAuthHeaders(jwt, currentUserPublicKey),
      params: {
        limit,
      },
    },
  );
  return data;
}

interface CandlestickHistoryRequest {
  symbol: string;
  to: number;
  resolution: "1M" | "5M" | "15M" | "1H" | "3H" | "1D" | "1W" | "4W";
  countback: number;
  quoteSymbol: string;
}

interface Candlestick {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  timestamp: string;
}

interface CandlestickHistoryResponse {
  candlesticks: Candlestick[];
}

export const getCandlestickHistory = async (
  params: CandlestickHistoryRequest,
): Promise<CandlestickHistoryResponse> => {
  const { symbol, to, resolution, countback, quoteSymbol } = params;

  const { data } = await focusAPIInstance.get(`tokens/candlesticks/history`, {
    params: {
      symbol,
      to,
      resolution,
      countback,
      quoteSymbol,
    },
  });

  return { candlesticks: data };
};

export const getRewardsClaimsCSV = async (currentUserPublicKey: string) => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.get(
    `rewards/claims/csv`,
    getFocusAuthHeaders(jwt, currentUserPublicKey),
  );
  return data;
};

export const createDefaultMessagingGroup = async (publicKey: string) => {
  const snapshot = await identity.snapshot();
  const messagingPublicKey =
    snapshot?.currentUser?.primaryDerivedKey.messagingPublicKeyBase58Check;

  if (!messagingPublicKey) {
    throw new Error("Messaging public key is missing.");
  }

  const resp = await createAccessGroup({
    AccessGroupOwnerPublicKeyBase58Check: publicKey,
    AccessGroupKeyName: DEFAULT_MESSAGING_GROUP_KEY_NAME,
    AccessGroupPublicKeyBase58Check: messagingPublicKey,
  });

  if (!resp.submittedTransactionResponse?.TxnHashHex) {
    throw new Error(
      "Transaction was submitted but no transaction hash was returned.",
    );
  }

  await waitForTxn({
    transactionHash: resp.submittedTransactionResponse?.TxnHashHex,
  });
};
export interface VerifierAdminRequest {
  publicKey: string;
  adminPublicKey: string;
}

export interface VerifierAdminResponse {
  message: string;
  publicKey: string;
  transactionHash: string;
}

export const authorizeVerifier = async (
  payload: VerifierAdminRequest,
): Promise<VerifierAdminResponse> => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.post<VerifierAdminResponse>(
    `/admin/verifier`,
    payload,
    getFocusAuthHeaders(jwt, payload.adminPublicKey),
  );
  return data;
};

export const deauthorizeVerifier = async (
  payload: VerifierAdminRequest,
): Promise<VerifierAdminResponse> => {
  const jwt = await identity.jwt();
  const { data } = await focusAPIInstance.delete<VerifierAdminResponse>(
    `/admin/verifier`,
    {
      data: payload,
      ...getFocusAuthHeaders(jwt, payload.adminPublicKey),
    },
  );
  return data;
};

export interface SendBulkMessagesRequest {
  AccessGroupOwnerPublicKey: string;
  AccessGroupKeyName: string;
  Message: string;
}

export interface SendBulkMessagesResponse {
  SubmitAtomicTransactionResponse: SubmitAtomicTransactionResponse;
}

export const sendBulkMessages = async (
  payload: SendBulkMessagesRequest,
): Promise<SendBulkMessagesResponse> => {
  const res = await focusAPIInstance.post<SendBulkMessagesResponse>(
    `message/bulk`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
  return res.data;
};

export interface DerivedKeyResponse {
  AccessGroupKeyName: string;
  AccessGroupOwnerPublicKey: string;
  AccessSignature: string;
  CreatedAt: string;
  DeletedAt: string;
  DerivedKeyType: string;
  DerivedPublicKey: string;
  EncryptedDerivedPrivateKey: string;
  ExpirationBlock: number;
  OwnerPublicKey: string;
  Status: string;
  TransactionSpendingLimitHex: string;
}

export async function getDerivedKey({
  derivedKeyType,
  ownerPublicKey,
}: {
  derivedKeyType: string;
  ownerPublicKey: string;
}): Promise<DerivedKeyResponse> {
  const { data } = await focusAPIInstance.get<DerivedKeyResponse>(
    `/derived-key`,
    {
      params: {
        derivedKeyType,
        ownerPublicKey,
      },
      ...(await getFocusAuthHeadersAsync()),
    },
  );
  return data;
}

export interface WelcomeMessage {
  AccessGroupKeyName: string;
  AccessGroupOwnerPublicKey: string;
  CreatedAt: string;
  Message: string;
}

interface GetWelcomeMessageRequest {
  accessGroupOwnerPublicKey: string;
  accessGroupKeyName: string;
}

interface CreateWelcomeMessageRequest {
  accessGroupOwnerPublicKey: string;
  accessGroupKeyName: string;
  message: string;
}

interface DeleteWelcomeMessageRequest {
  accessGroupOwnerPublicKey: string;
  accessGroupKeyName: string;
}

export const getWelcomeMessage = async (
  payload: GetWelcomeMessageRequest,
): Promise<WelcomeMessage> => {
  const { data } = await focusAPIInstance.get<WelcomeMessage>(
    `/message/welcome`,
    {
      params: payload,
      ...(await getFocusAuthHeadersAsync()),
    },
  );
  return data;
};

export const createWelcomeMessage = async (
  payload: CreateWelcomeMessageRequest,
): Promise<WelcomeMessage> => {
  const { data } = await focusAPIInstance.put<WelcomeMessage>(
    `/message/welcome`,
    payload,
    await getFocusAuthHeadersAsync(),
  );
  return data;
};

export const deleteWelcomeMessage = async (
  payload: DeleteWelcomeMessageRequest,
): Promise<string> => {
  const { data } = await focusAPIInstance.delete<string>(`/message/welcome`, {
    params: payload,
    ...(await getFocusAuthHeadersAsync()),
  });
  return data;
};
